import { Head, Link } from "@inertiajs/react"
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  FileText,
  History,
  Play,
} from "lucide-react"
import { marked } from "marked"

import { ChatInterface } from "@/components/chat-interface"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { VersionHistory } from "@/components/version-history"
import AppLayout from "@/layouts/app-layout"

interface ShowScenarioProps {
  scenario: {
    id: string
    name: string
    content: string
    description: string
    lastModified: string
    agentic: boolean
  }
  team_id: string
  org_id: string
}

const ShowScenario: React.FC<ShowScenarioProps> = ({
  scenario,
  team_id,
  org_id,
}: ShowScenarioProps) => {
  return (
    <AppLayout>
      <Head title={scenario.name} />

      <div className="p-6">
        <div className="mb-6">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Link
                href={`/orgs/${org_id}/teams/${team_id}`}
                className="text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="size-4" />
              </Link>
              <FileText className="text-primary size-6" />
              <div>
                <h1 className="text-3xl font-bold tracking-tight">
                  {scenario.name}
                </h1>
                <p className="text-muted-foreground">{scenario.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" className="gap-2">
                <History className="size-4" />
                View History
              </Button>
              {scenario.agentic && (
                <Button size="lg" className="gap-2">
                  <Play className="size-4" />
                  Run Agent
                </Button>
              )}
            </div>
          </div>

          <div className="text-muted-foreground flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <Clock className="size-4" />
              <span>Last modified: {scenario.lastModified}</span>
            </div>
            <Badge variant="secondary">Instructions</Badge>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-4">
          <div className="lg:col-span-3">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="text-primary size-5" />
                  Scenario Instructions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: marked.parse(scenario.content),
                  }}
                />
              </CardContent>
            </Card>
          </div>

          <div className="flex flex-col gap-4">
            <ChatInterface context={scenario.name} />
            <VersionHistory fileId={scenario.id} />
          </div>
        </div>
      </div>
    </AppLayout>
  )
}

export default ShowScenario
